import { useMemo, useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK } from "@/lib/app";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";

const fetcher = (url) => api.get(url).then((r) => r.data);

const HourlyTooltip = ({ active, payload, label, requiredPerformance, chartMetric }) => {
  if (!active || !payload || !payload.length) return null;
  const r = payload[0].payload || {};
  const ratio = (r.per_person_hour || 0) / (requiredPerformance || 1);
  const color = ratio >= 1 ? "#3E7B52" : ratio >= 0.7 ? "#C78220" : "#BA3F2B";
  return (
    <div className="bg-white border border-[var(--border-default)] rounded shadow-sm text-xs p-2.5 min-w-[200px]" style={{ fontSize: 12 }}>
      <div className="font-semibold mb-1.5">Hodina {label}-{label + 1}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 tabular">
        {chartMetric === "customers" ? (
          <>
            <span className="text-[var(--text-muted)]">Nákupy ø/výskyt</span>
            <span className="text-right font-medium">{fmtNum(r.avg_customers_per_weekday, 1)}</span>
            <span className="text-[var(--text-muted)]">Priem. košík</span>
            <span className="text-right font-medium">{fmtEur(r.basket)}</span>
          </>
        ) : null}
        <span className="text-[var(--text-muted)]">Tržba ø/výskyt</span>
        <span className="text-right font-medium">{fmtEur(r.avg_revenue_per_weekday)}</span>
        <span className="text-[var(--text-muted)]">Ľudia ø</span>
        <span className="text-right font-medium">{fmtNum(r.employees, 1)}</span>
        <span className="text-[var(--text-muted)]">€/os.hod</span>
        <span className="text-right font-medium" style={{ color }}>{fmtEur(r.per_person_hour)}</span>
        <span className="text-[var(--text-muted)]">Cieľ</span>
        <span className="text-right text-[var(--text-muted)]">{fmtEur(requiredPerformance)}</span>
        <span className="text-[var(--text-muted)]">Výskyt</span>
        <span className="text-right text-[var(--text-muted)]">{r.count}/{r.weekday_days} ({r.frequency_pct}%)</span>
      </div>
    </div>
  );
};

export default function HourlyBreakdown({ storeId, period, store }) {
  const [localPeriod, setLocalPeriod] = useState(period);
  const [chartMetric, setChartMetric] = useState("revenue"); // "revenue" | "customers"
  const [tableWeekday, setTableWeekday] = useState("all"); // "all" | 0..6
  const qs = `period_from=${localPeriod?.from || ""}&period_to=${localPeriod?.to || ""}`;
  const { data } = useSWR(`/stores/${storeId}/analytics/hourly?${qs}`, fetcher);

  const grouped = useMemo(() => {
    if (!data?.rows) return [];
    const by = new Map();
    for (const r of data.rows) {
      if (!by.has(r.weekday)) by.set(r.weekday, []);
      by.get(r.weekday).push(r);
    }
    return Array.from(by.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([wd, rows]) => ({
        weekday: wd,
        rows: rows.sort((a, b) => a.hour - b.hour),
      }));
  }, [data]);

  if (!data) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie hodinového profilu" testIdPrefix="hourly-period" />
      <div className="text-[var(--text-secondary)]">Načítavam...</div>
    </div>
  );
  if (data.empty) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie hodinového profilu" testIdPrefix="hourly-period" />
      <div className="card-flat p-8 text-center text-[var(--text-secondary)]">V tomto období nie sú dáta.</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie hodinového profilu" testIdPrefix="hourly-period" />
      <div className="card-flat p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-base font-semibold">Hodinový profil podľa dňa v týždni</h3>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-[var(--border-default)] overflow-hidden text-xs" data-testid="hourly-metric-toggle">
              <button
                className={`px-3 py-1.5 ${chartMetric === "revenue" ? "bg-[var(--bg-surface-hover)] font-semibold" : "text-[var(--text-secondary)]"}`}
                onClick={() => setChartMetric("revenue")}
                data-testid="hourly-metric-revenue"
              >
                Tržba
              </button>
              <button
                className={`px-3 py-1.5 border-l border-[var(--border-default)] ${chartMetric === "customers" ? "bg-[var(--bg-surface-hover)] font-semibold" : "text-[var(--text-secondary)]"}`}
                onClick={() => setChartMetric("customers")}
                data-testid="hourly-metric-customers"
              >
                Nákupy
              </button>
            </div>
            <PinButton
              storeId={storeId}
              kind="chart"
              label={`Hodinový profil — všetky dni (${period.from} → ${period.to})`}
              data={{ id: `hourly_profile_${period.from}_${period.to}`, source: "hourly_breakdown_chart" }}
              period={period}
              testId="pin-chart-hourly"
            />
          </div>
        </div>
        <p className="text-xs text-[var(--text-secondary)] mt-1">
          {chartMetric === "revenue" ? (
            <>
              <strong>Bary</strong> = priemerná tržba na 1 výskyt dňa (sum tržby ÷ počet všetkých Po/Ut/… v období) — sloty s riedkou prevádzkou sú prirodzene nižšie.{" "}
              <strong>Farba</strong> = produktivita €/os.hod vs požadovaný výkon {fmtEur(data.required_performance)}.
            </>
          ) : (
            <>
              <strong>Bary</strong> = priemerný počet nákupov (zákazníkov) na 1 výskyt dňa.{" "}
              <strong>Farba</strong> = priemerný košík — tmavšia = drahší nákup.
            </>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {grouped.map((g) => {
          const wdDays = g.rows[0]?.weekday_days || 0;
          return (
          <div key={g.weekday} className="card-flat p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <h4 className="font-display text-sm font-semibold">{WEEKDAYS_SK[g.weekday]}</h4>
                <PinButton
                  storeId={storeId}
                  kind="chart"
                  label={`Hodinový profil — ${WEEKDAYS_SK[g.weekday]} (${period.from} → ${period.to})`}
                  data={{ id: `hourly_${g.weekday}_${period.from}_${period.to}`, weekday: g.weekday, source: "hourly_weekday_chart" }}
                  period={period}
                  testId={`pin-chart-hourly-${g.weekday}`}
                />
              </div>
              <span className="text-xs text-[var(--text-muted)] tabular">{wdDays} {wdDays === 1 ? "deň" : wdDays < 5 ? "dni" : "dní"}</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={g.rows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#5E6962" }} tickFormatter={(v) => `${v}-${v + 1}`} />
                <YAxis tick={{ fontSize: 10, fill: "#5E6962" }} tickFormatter={(v) => chartMetric === "revenue" ? `${v}€` : `${v}`} />
                <Tooltip
                  cursor={{ fill: "rgba(43,76,56,0.06)" }}
                  content={<HourlyTooltip requiredPerformance={data.required_performance} chartMetric={chartMetric} />}
                />
                <Bar dataKey={chartMetric === "revenue" ? "avg_revenue_per_weekday" : "avg_customers_per_weekday"} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  {g.rows.map((r, i) => {
                    let c;
                    if (chartMetric === "revenue") {
                      const ratio = r.per_person_hour / data.required_performance;
                      c = ratio >= 1 ? "#3E7B52" : ratio >= 0.7 ? "#C78220" : "#BA3F2B";
                    } else {
                      // color by basket size — richer = darker green
                      const b = r.basket || 0;
                      c = b >= 15 ? "#1E4B2E" : b >= 12 ? "#3E7B52" : b >= 9 ? "#7FA689" : "#B6C9BB";
                    }
                    return <Cell key={i} fill={c} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          );
        })}
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-sm font-semibold">Tabuľka — priemer (deň × hodina)</h3>
            <p className="text-xs text-[var(--text-secondary)]">Tržba/deň = sum ÷ všetky výskyty daného dňa · €/os.hod = produktivita v hodinách kedy bolo otvorené.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[var(--text-secondary)]">Zobrazte deň:</label>
            <select
              className="text-xs border border-[var(--border-default)] rounded px-2 py-1 bg-white"
              value={tableWeekday}
              onChange={(e) => setTableWeekday(e.target.value === "all" ? "all" : Number(e.target.value))}
              data-testid="hourly-table-weekday-filter"
            >
              <option value="all">Všetky dni</option>
              {WEEKDAYS_SK.map((name, i) => (
                <option key={i} value={i}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto scroll-area">
          <table>
            <thead>
              <tr>
                <th>Deň</th>
                <th>Hodina</th>
                <th className="text-right">Tržba/deň</th>
                <th className="text-right">Tržba ø/výskyt</th>
                <th className="text-right">Nákupy/deň</th>
                <th className="text-right">Košík</th>
                <th className="text-right">Ľudia ø</th>
                <th className="text-right">€/os.hod</th>
                <th className="text-right" title="Výskyt slotu / celkové výskyty dňa">Výskyt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows
                .filter((r) => tableWeekday === "all" || r.weekday === tableWeekday)
                .map((r, idx) => {
                const ratio = r.per_person_hour / data.required_performance;
                const cls = ratio >= 1 ? "status-good" : ratio >= 0.7 ? "status-warn" : "status-bad";
                const lowFreq = r.frequency_pct < 50;
                return (
                  <tr key={idx}>
                    <td>{WEEKDAYS_SK[r.weekday]}</td>
                    <td className="tabular">{r.hour}-{r.hour + 1}</td>
                    <td className="text-right tabular font-medium">{fmtEur(r.avg_revenue_per_weekday)}</td>
                    <td className="text-right tabular text-[var(--text-muted)]">{fmtEur(r.revenue)}</td>
                    <td className="text-right tabular">{fmtNum(r.avg_customers_per_weekday || 0, 1)}</td>
                    <td className="text-right tabular text-[var(--text-muted)]">{fmtEur(r.basket || 0)}</td>
                    <td className="text-right tabular">{fmtNum(r.employees, 1)}</td>
                    <td className={`text-right tabular px-2 rounded ${cls}`}>{fmtEur(r.per_person_hour)}</td>
                    <td className={`text-right tabular ${lowFreq ? "text-[var(--warning)] font-medium" : "text-[var(--text-muted)]"}`} title={`${r.count} z ${r.weekday_days} dní`}>
                      {r.count}/{r.weekday_days}
                    </td>
                    <td className="text-right">
                      <PinButton
                        storeId={storeId}
                        kind="weekday"
                        label={`${WEEKDAYS_SK[r.weekday]} ${r.hour}-${r.hour + 1}: Tržba/deň ${fmtEur(r.avg_revenue_per_weekday)}, €/os.hod ${fmtEur(r.per_person_hour)}, výskyt ${r.count}/${r.weekday_days}`}
                        data={{ weekday: r.weekday, hour: r.hour, source: "hourly_profile", ...r }}
                        period={period}
                        testId={`pin-hourly-${r.weekday}-${r.hour}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
